import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;
const serif = { fontFamily: "'Playfair Display', serif" };
const script = { fontFamily: "'Great Vibes', cursive" };
const sans = { fontFamily: "'Jost', sans-serif" };

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [data, setData] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        const d = res.data;
        setData(d);
        if (d.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (d.payment_status === "expired" || d.payment_status === "failed") {
          setState("failed");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) return setState("timeout");
        setTimeout(poll, INTERVAL);
      } catch {
        if (!active) return;
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) return setState("timeout");
        setTimeout(poll, INTERVAL);
      }
    };

    poll();
    return () => {
      active = false;
    };
  }, [sessionId, clearCart]);

  return (
    <div data-testid="success-page" className="pt-40 pb-24 px-6 bg-[#F8F5F0]">
      <div className="max-w-xl mx-auto text-center">
        {state === "checking" && (
          <div data-testid="status-checking">
            <Loader2 className="w-12 h-12 text-[#C6A769] mx-auto animate-spin" strokeWidth={1.2} />
            <h1 style={serif} className="text-4xl text-[#4A3F35] mt-8">Confirming your payment…</h1>
            <p style={sans} className="text-[#8A7D71] mt-3">One elegant moment, please.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="status-paid" className="fade-up">
            <CheckCircle2 className="w-14 h-14 text-[#C6A769] mx-auto" strokeWidth={1.2} />
            <p style={sans} className="text-xs uppercase tracking-[0.3em] text-[#C6A769] mt-6">Order Confirmed</p>
            <h1 style={serif} className="text-5xl text-[#4A3F35] mt-4">
              Thank you, <span style={script} className="text-[#C6A769]">beautiful.</span>
            </h1>
            <p style={sans} className="text-[#8A7D71] mt-4 leading-relaxed">
              Your purchase is complete{data?.email ? <> and a copy is on its way to <strong>{data.email}</strong></> : ""}.
              Download your products instantly below.
            </p>

            <div className="mt-10 space-y-4 text-left">
              {data?.downloads?.map((d, i) => (
                <a
                  key={i}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`download-link-${i}`}
                  className="flex items-center justify-between gap-4 bg-[#FAF7F2] border border-[#4A3F35]/10 rounded-sm px-6 py-5 hover:border-[#C6A769] transition-colors duration-300 group"
                >
                  <div>
                    <p style={sans} className="text-[10px] uppercase tracking-[0.2em] text-[#C6A769]">PDF Download</p>
                    <p style={serif} className="text-xl text-[#4A3F35]">{d.display_name}</p>
                  </div>
                  <span style={sans} className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.12em] text-[#4A3F35] group-hover:text-[#C6A769] transition-colors">
                    <Download className="w-4 h-4" /> Download
                  </span>
                </a>
              ))}
            </div>

            {data?.downloads?.length > 0 && (
              <p style={sans} data-testid="download-expiry-note" className="text-xs text-[#8A7D71] mt-6">
                Your links stay active for 30 days and allow up to 5 downloads each — save your files somewhere safe.
              </p>
            )}

            <p style={sans} data-testid="email-timing-note" className="text-xs text-[#8A7D71] mt-3">
              Your confirmation email can take a few minutes to arrive — check your spam folder too.
              No need to wait: your files are ready to download right here.
            </p>

            <Link
              to="/shop"
              data-testid="continue-shopping"
              style={sans}
              className="mt-12 inline-block text-sm uppercase tracking-[0.15em] text-[#4A3F35] border-b border-[#4A3F35]/40 pb-1 hover:text-[#C6A769] hover:border-[#C6A769] transition-colors duration-300"
            >
              Continue Shopping
            </Link>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="status-failed">
            <XCircle className="w-14 h-14 text-[#4A3F35]/50 mx-auto" strokeWidth={1.2} />
            <h1 style={serif} className="text-4xl text-[#4A3F35] mt-8">
              {state === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p style={sans} className="text-[#8A7D71] mt-3 leading-relaxed">
              {state === "timeout"
                ? "Your payment is taking a little longer than usual. If you were charged, check your email for your download links."
                : "We couldn't confirm a completed payment. Please try again."}
            </p>
            <Link
              to="/cart"
              data-testid="back-to-cart"
              style={sans}
              className="mt-8 inline-block bg-[#4A3F35] text-[#FAF7F2] px-9 py-4 rounded-sm text-sm uppercase tracking-[0.15em] hover:bg-[#C6A769] transition-colors duration-500"
            >
              Return to Cart
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Success;
